import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';
import { canAssignClients } from '../../lib/roles';
import { useFeatures } from '../../lib/features';
import { contractState, todayKey } from '../../lib/clientPage';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { useNavigate } from 'react-router-dom';
import {
  Phone, Mail, MapPin, Globe, User, UserCheck, Plus, Hand, Edit, Loader2, Hash, MessageSquare,
  ArrowRightLeft, Hourglass, ExternalLink, FileText,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { showDay, StatusPill, apiError } from './salesShared';
import { ClientHistoryList, clientEvents } from './ClientHistoryList';

// One client, everything in date order: details, activities, opportunities
// and docket numbers.
const ClientTimelineModal = ({ clientId, open, onClose, onLog, onEdit, onChanged }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { salesOnly, clientLabel } = useFeatures();
  const isSalesRep = user?.role === 'sales_rep';
  // Admins, accounting and sales admins give a client to another rep.
  const canAssign = canAssignClients(user);
  const [reps, setReps] = useState([]);
  const [reassigning, setReassigning] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [claiming, setClaiming] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError('');
    try {
      setData(await api.get(`/advertisers/${clientId}/timeline`));
    } catch (e) {
      setError(apiError(e, 'Could not load this client'));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (open) { setData(null); load(); }
  }, [open, load]);

  // Admins can hand the client to another rep at any time.
  useEffect(() => {
    let alive = true;
    if (open && canAssign) {
      api.get('/sales-reps').then(r => { if (alive) setReps(r || []); }).catch(() => {});
    }
    return () => { alive = false; };
  }, [open, canAssign]);

  const reassign = async (repId) => {
    setReassigning(true);
    setError('');
    try {
      await api.post('/advertisers/bulk-assign', { sales_rep_id: repId === 'none' ? null : repId, ids: [clientId] });
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(apiError(e, 'Could not reassign this client'));
    } finally {
      setReassigning(false);
    }
  };

  const claim = async () => {
    setClaiming(true);
    try {
      await api.post(`/advertisers/${clientId}/claim`, {});
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(apiError(e, 'Could not claim this client'));
    } finally {
      setClaiming(false);
    }
  };

  const c = data?.client;
  const unassigned = c && !c.assigned_sales_rep_id;
  // Another rep's client: details and rep are visible, the history is not,
  // and only its own rep can log or edit.
  const othersClient = !!data?.history_hidden;
  // Activities, opportunities and assignment changes merged, newest first.
  const events = clientEvents(data);

  const Line = ({ icon: Icon, children }) => (
    <div className="flex items-start gap-2 text-sm text-[#94A3B8]"><Icon className="w-4 h-4 mt-0.5 shrink-0" /><span className="min-w-0 break-words">{children}</span></div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="client-timeline-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap pr-6">
            <span data-testid="timeline-client-name">{c?.business_name || clientLabel}</span>
            {c && <StatusPill status={c.sales_status} />}
          </DialogTitle>
          <DialogDescription className="text-[#94A3B8]">
            {c?.industry || 'No industry'}{c?.location_type ? ` · ${c.location_type}` : ''}
          </DialogDescription>
        </DialogHeader>

        {loading && !data && <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#3B82F6]" /></div>}
        {error && <div className="text-sm text-red-400 bg-red-900/20 border border-red-800 rounded-md p-2" data-testid="timeline-error">{error}</div>}

        {c && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
              {c.phone && <Line icon={Phone}><a href={`tel:${c.phone}`} className="text-[#3B82F6]" data-testid="timeline-phone">{c.phone}</a>{c.alt_phone ? ` · ${c.alt_phone}` : ''}</Line>}
              {c.email && <Line icon={Mail}><a href={`mailto:${c.email}`} className="text-[#3B82F6]">{c.email}</a></Line>}
              {c.contact_name && <Line icon={User}>{c.contact_name}</Line>}
              {c.created_by === 'newsdesk' && <Line icon={Mail}><span data-testid="timeline-newsdesk-origin">Created from an email in Newsdesk{c.created_by_user ? ` by ${c.created_by_user}` : ''}</span></Line>}
              {c.address && <Line icon={MapPin}>{c.address}</Line>}
              {c.website && <Line icon={Globe}><a href={c.website} target="_blank" rel="noreferrer" className="text-[#3B82F6]">{c.website}</a></Line>}
              <Line icon={UserCheck}>
                {data.sales_rep ? `Sales rep: ${data.sales_rep.name}` : <span className="text-amber-400">Unassigned (available)</span>}
              </Line>
              {contract && (
                <Line icon={FileText}>
                  <span data-testid="timeline-contract">{contract === 'active'
                    ? `Under contract until ${showDay(c.contract_end_date)}`
                    : `Contract ended ${showDay(c.contract_end_date)}`}</span>
                </Line>
              )}
              {data.days_until_release != null && (
                <Line icon={Hourglass}>
                  <span className={data.days_until_release <= 14 ? 'text-amber-400' : ''} data-testid="timeline-release-countdown">
                    {data.days_until_release === 0
                      ? 'Goes back to the unassigned pool at the next daily check unless an activity is logged'
                      : `Goes back to the unassigned pool in ${data.days_until_release} day${data.days_until_release === 1 ? '' : 's'} without a logged activity`}
                  </span>
                </Line>
              )}
              {c.docket_numbers && <Line icon={Hash}>Dockets: <span className="font-mono text-[#F8FAFC]" data-testid="timeline-dockets">{c.docket_numbers}</span></Line>}
              {c.next_follow_up_date && <Line icon={MessageSquare}>Next follow-up: {showDay(c.next_follow_up_date)}</Line>}
            </div>
            {!c.phone && !c.email && (
              <p className="text-xs text-amber-400">No phone or email on file. Add one from Sales → Needs details.</p>
            )}
            {c.notes && <p className="text-xs text-[#94A3B8] whitespace-pre-wrap bg-[#0A0A0A] border border-[#1E293B] rounded p-3" data-testid="timeline-notes">{c.notes}</p>}

            <div className="flex flex-wrap gap-2">
              {onLog && !othersClient && (
                <Button size="sm" onClick={() => onLog(c)} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="timeline-log-btn">
                  <Plus className="w-4 h-4 mr-1" /> Log activity
                </Button>
              )}
              {isSalesRep && unassigned && (
                <Button size="sm" variant="outline" onClick={claim} disabled={claiming} className="border-[#1E293B] bg-[#121212] text-[#F8FAFC]" data-testid="timeline-claim-btn">
                  <Hand className="w-4 h-4 mr-1" /> {claiming ? 'Claiming…' : 'Claim this client'}
                </Button>
              )}
              {canAssign && (
                <div className="flex items-center gap-2" data-testid="timeline-reassign">
                  <ArrowRightLeft className="w-4 h-4 text-[#94A3B8]" />
                  <Select value={c.assigned_sales_rep_id || 'none'} onValueChange={reassign} disabled={reassigning}>
                    <SelectTrigger className="h-9 w-[190px] bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] text-sm" data-testid="timeline-reassign-select">
                      <SelectValue placeholder="Assign to…" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#1E293B]">
                      <SelectItem value="none" className="text-[#F8FAFC]">Unassigned</SelectItem>
                      {reps.map(r => <SelectItem key={r.id} value={r.id} className="text-[#F8FAFC]">{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {reassigning && <Loader2 className="w-4 h-4 animate-spin text-[#3B82F6]" />}
                </div>
              )}
              <Button size="sm" variant="outline" onClick={() => { onClose(); navigate(`/advertisers/${clientId}`); }} className="border-[#1E293B] bg-[#121212] text-[#94A3B8]" data-testid="timeline-open-page-btn">
                <ExternalLink className="w-4 h-4 mr-1" /> Open {clientLabel.toLowerCase()} page
              </Button>
              {onEdit && !othersClient && (
                <Button size="sm" variant="outline" onClick={() => onEdit(c)} className="border-[#1E293B] bg-[#121212] text-[#94A3B8]" data-testid="timeline-edit-btn">
                  <Edit className="w-4 h-4 mr-1" /> Edit details
                </Button>
              )}
            </div>

            <div>
              <h3 className="text-sm font-semibold text-[#F8FAFC] mb-2">History ({events.length})</h3>
              <ClientHistoryList data={data} events={events} salesOnly={salesOnly} />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClientTimelineModal;
